import React, {
  useContext, useEffect, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  FormControl,
  Box,
  Grid,
  InputAdornment,
  InputLabel,
  makeStyles,
  Select,
  TextField,
  Typography, LinearProgress,
} from '@material-ui/core';
import { DateRange, LocationOn } from '@material-ui/icons';
import { round } from 'lodash';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import Scheduler from './Scheduler';
import formatPhoneNumber from '../../helpers/format';
import CalendarWeekPicker from './CalendarWeekPicker';
import AppContext from '../../state/AppContext';
import { isLoadingAvailabilitiesState } from '../../state/apiRequestStatus';
import {
  preSelectedClinicIdState,
  selectedClinicAvailabilitiesFilteredState,
  selectedClinicIdState, selectedDayjsState, selectedSchedulerTherapistIdState,
} from '../../state/selected';
import availableClinicIdsState from '../../state/apiData';

const selectPinColor = ({
  isClinicUnavailable,
  isClinicSelected,
}, colors) => {
  if (isClinicSelected) {
    return colors[0];
  }
  if (isClinicUnavailable) {
    return colors[1];
  }
  return colors[2];
};

const useStyles = makeStyles((theme) => ({
  clinic: {
    boxShadow: (props) => (props.isClinicSelected ? `${theme.palette.common.black} 0 0 0 4px inset` : 'none'),
    border: (props) => (props.isClinicSelected ? `1px solid ${theme.palette.common.black}` : `1px solid ${theme.palette.grey[300]}`),
    marginBottom: theme.spacing(2),
  },
  clinicHeaderWrapper: {
    backgroundColor: theme.palette.primary.light,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(2),
    marginRight: (props) => (props.isClinicSelected ? '4px' : 0),
    marginLeft: (props) => (props.isClinicSelected ? '4px' : 0),
  },
  clinicHeader: {
    padding: theme.spacing(2),
    display: 'flex',
  },
  iconClinic: {
    color: (props) => selectPinColor(
      props, [theme.palette.common.black, theme.palette.grey[500], theme.palette.primary.main],
    ),
  },
  iconWrapper: {
    flex: '0 0 40px',
  },
  contentWrapper: {
    flex: '1 0',
  },
  clinicHeaderColRight: {
    textAlign: 'right',
  },
  weekField: {
    '& > div': { backgroundColor: theme.palette.background.default },
  },
  specialistsField: {
    '& > div': { backgroundColor: theme.palette.background.default },
  },
  phoneLink: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    textDecoration: 'none',
  },
  progressBar: {
    flex: '1 0 100%',
    marginTop: theme.spacing(2),
  },
}));

const ClinicScheduler = (props) => {
  const {
    schedulerTherapists,
  } = useContext(AppContext);

  const selectedClinicAvailabilitiesFiltered = useRecoilValue(
    selectedClinicAvailabilitiesFilteredState,
  );
  const availableClinicIds = useRecoilValue(availableClinicIdsState);
  const preSelectedClinicId = useRecoilValue(preSelectedClinicIdState);
  const [
    selectedDayjs, setSelectedDayjs,
  ] = useRecoilState(selectedDayjsState);
  const [
    isLoadingAvailabilities, setIsLoadingAvailabilities,
  ] = useRecoilState(isLoadingAvailabilitiesState);
  const [
    selectedClinicId, setSelectedClinicId,
  ] = useRecoilState(selectedClinicIdState);
  const [
    selectedSchedulerTherapistId, setSelectedSchedulerTherapistId,
  ] = useRecoilState(selectedSchedulerTherapistIdState);

  const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);

  const intl = useIntl();

  const {
    marker,
  } = props;

  const clinicElem = useRef(null);

  const c = marker.clinic;

  const isClinicUnavailable = availableClinicIds.filter((cl) => (cl === c.id)).length === 0;
  const isClinicSelected = selectedClinicId === c.id;
  const classes = useStyles({
    isClinicUnavailable,
    isClinicSelected,
  });

  useEffect(() => {
    const today = dayjs();
    const isFirstWeekSelected = selectedDayjs.isSameOrAfter(today.day(1), 'day')
      && selectedDayjs.isSameOrBefore(today.day(7), 'day');

    if (!isLoadingAvailabilities
      && selectedClinicId === c.id
      && isFirstWeekSelected
      && selectedClinicAvailabilitiesFiltered.length > 0) {
      let isFirstWeekDisabled = true;
      Array(5)
        .fill(0)
        .map((day, i) => selectedDayjs.day(i + 1)
          .format('YYYY-MM-DD'))
        .map((day) => {
          if (isFirstWeekDisabled
            && selectedClinicAvailabilitiesFiltered.filter((av) => av.date === day).length > 0) {
            isFirstWeekDisabled = false;
          }
          return null;
        });

      setIsWeekSelectorOpen(isFirstWeekDisabled);
    }
  }, [isLoadingAvailabilities, selectedClinicAvailabilitiesFiltered]);

  useEffect(() => {
    if (isClinicSelected && selectedClinicId !== preSelectedClinicId) {
      clinicElem.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [selectedClinicId]);

  const renderWeekPickerOrSchedduler = () => {
    if (isWeekSelectorOpen) {
      return (
        <CalendarWeekPicker callbackSelection={() => {
          setIsWeekSelectorOpen(false);
        }}
        />
      );
    }
    return (<Scheduler />);
  };

  return (
    <div className={classes.clinic} ref={clinicElem} key={c.id}>
      <div className={classes.clinicHeader}>
        <div className={classes.iconWrapper}>
          <LocationOn className={classes.iconClinic} fontSize="large" />
        </div>
        <div className={classes.contentWrapper}>
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <Typography component="div" variant="h6">
                <strong>{c.nom}</strong>
              </Typography>
              <Typography component="div">
                {c.adr_no}
                {' '}
                {c.adr_rue}
              </Typography>
              <Typography component="div">{c.adr_app}</Typography>
              <Typography component="div">
                {c.adr_ville}
                ,
                {' '}
                {c.adr_province}
                {' '}
                {c.adr_cp}
              </Typography>
              <Typography component="div">
                <a href={`tel:+1${c.tel}`} className={classes.phoneLink}>
                  {formatPhoneNumber(c.tel)}
                </a>
              </Typography>
            </Grid>
            <Grid
              className={classes.clinicHeaderColRight}
              item
              xs={12}
              md={6}
            >
              <Typography component="div" variant="body2">
                {round(marker.distance / 1000, 1)}
                &nbsp;km
              </Typography>
              <Typography component="div">
                <FormattedMessage id="ClinicScheduler.fromYourLocation" />
              </Typography>
              {(selectedClinicId !== marker.clinic.id || isClinicUnavailable) && (
                <Box mt={1}>
                  <Button
                    fullWidth
                    variant={isClinicUnavailable ? 'outlined' : 'contained'}
                    color="primary"
                    disabled={isClinicUnavailable}
                    onClick={() => {
                      setIsLoadingAvailabilities(true);
                      setSelectedClinicId(marker.clinic.id);
                      setSelectedDayjs(dayjs());
                    }}
                  >
                    {isClinicUnavailable
                      ? <FormattedMessage id="ClinicScheduler.noAvailabilities" />
                      : <FormattedMessage id="ClinicScheduler.showAppointments" />}
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
      {(!isClinicUnavailable && selectedClinicId === marker.clinic.id) && (
        <div>
          <div className={classes.clinicHeaderWrapper}>
            <Grid
              className={classes.clinicHeader}
              container
              spacing={2}
              justifyContent="space-between"
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.weekField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRange />
                      </InputAdornment>),
                    readOnly: true,
                  }}
                  value={intl.formatMessage({ id: 'ClinicScheduler.weekOf' }, {
                    date: dayjs(selectedDayjs.day(1))
                      .format('ll'),
                  })}
                  onClick={() => {
                    setIsWeekSelectorOpen(true);
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <FormControl
                  className={classes.specialistsField}
                  variant="outlined"
                  fullWidth
                  size="small"
                >
                  <InputLabel htmlFor="specialists">
                    <FormattedMessage id="ClinicScheduler.specialists" />
                  </InputLabel>
                  <Select
                    native
                    label={<FormattedMessage id="ClinicScheduler.specialists" />}
                    inputProps={{
                      name: 'specialists',
                      id: 'specialists',

                    }}
                    value={selectedSchedulerTherapistId}
                    disabled={isLoadingAvailabilities || schedulerTherapists.length < 2}
                    onChange={(e) => {
                      setSelectedSchedulerTherapistId(e.target.value);
                    }}
                  >
                    {schedulerTherapists.length > 1 && <option aria-label="None" value="" />}
                    {schedulerTherapists.map((t) => (
                      <option value={t.id} key={t.id}>{t.name}</option>))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </div>
          {isLoadingAvailabilities
            ? (
              <Box m={3}>
                <LinearProgress className={classes.progressBar} />
              </Box>
            ) : renderWeekPickerOrSchedduler()}
        </div>

      )}
    </div>
  );
};

ClinicScheduler.propTypes = {
  marker: PropTypes.shape({
    clinic: PropTypes.shape({
      id: PropTypes.number,
      nom: PropTypes.string,
      adr_no: PropTypes.string,
      adr_rue: PropTypes.string,
      adr_app: PropTypes.string,
      adr_ville: PropTypes.string,
      adr_province: PropTypes.string,
      adr_cp: PropTypes.string,
      tel: PropTypes.string,
    }),
    distance: PropTypes.number,
  }).isRequired,
};

export default ClinicScheduler;
